import React, {FC, useMemo} from 'react';
import {View, StyleSheet} from 'react-native';
import {DeviceSize} from '../../../shared/theme/helpers';
import {Field} from 'redux-form';
import {Button} from '../../../shared/components/button';
import {BaseField, InputMaskTypes} from '../../../shared/components/baseField';
import {useStyles, StyleHelper, NamedStyles} from '../../../shared/hooks/styles';
import {images} from '../../../assets/icons';
import {defaults} from '../../../shared/constants/defaults';
import {LoginType, SendCodeChannelType} from '../../../shared/models/api';
import {Form} from '../../../shared/components/form';
import {QrCodeIcon} from '../components/qrCodeIcon';
import {formatIdentificator, formatLoginLabel} from '../formatters';
import {useIsDesktop} from '../../../shared/hooks/useIsDesktop';
import {IconProps} from '../../../shared/components/svg/model';
import {SmsFields} from '../registratonOrRecovery/smsFields';
import {PasswordFields} from '../registratonOrRecovery/passwordFields';
import {Agreement} from '../components/agreement';
import {Platform} from 'react-native';

const formWidthDesktop = 374;

export interface Props {
  disabled: boolean;
  onSubmit: (e?: any) => void;
  onRefresh: (channelType?: SendCodeChannelType) => Promise<boolean>;
  loginType?: LoginType;
  confirmBySnils?: boolean;
  confirmByCard?: boolean;
  needPhone?: boolean;
  needSmsCode?: boolean;
  needPassword?: boolean;
  needAgreement?: boolean;
  onAutoFill: (fieldName: string) => (data: any) => void;
  submitting: boolean;
  isConfirmButtonDisabled: boolean;
}

export default (props: Props) => {
  const styles = useStyles(createStyles);
  const {
    onSubmit,
    onRefresh,
    disabled,
    loginType,
    confirmBySnils,
    confirmByCard,
    needPhone,
    needSmsCode,
    needPassword,
    needAgreement,
    onAutoFill,
    submitting,
    isConfirmButtonDisabled,
  } = props;
  const isDesktop = useIsDesktop();

  const buttonProps: {
    text: string;
    icon?: FC<IconProps>;
    iconProps?: IconProps;
  } = useMemo(() => {
    if (needPassword) return {text: 'Войти'};
    else if ((needPhone || (confirmByCard && !needSmsCode)) && !needAgreement) return {text: 'Получить SMS-код'};
    return {text: 'Далее', iconProps: {width: 24, height: 24}, icon: images.ChevronRightThick};
  }, [needPhone, needSmsCode, needPassword, needAgreement, confirmByCard]);

  const enterWithSnils = confirmBySnils && !needPhone && !needSmsCode && !needAgreement && !needPassword;

  const identificatorFormatter = React.useMemo(() => {
    if (!loginType && Platform.OS === 'web') {
      return undefined;
    } else {
      return (v: any) => formatIdentificator(v, loginType);
    }
  }, [loginType]);

  return (
    <View>
      <Form style={styles.container} onSubmit={onSubmit}>
        {confirmBySnils && !needAgreement && !needPassword ? (
          <View style={styles.fieldContainer}>
            <Field
              component={BaseField}
              label={formatLoginLabel(loginType)}
              type='digits'
              name='identificator'
              style={isDesktop ? {width: formWidthDesktop, marginBottom: 10, marginTop: 24} : undefined}
              disabled={disabled || confirmBySnils}
              maxLength={defaults.maxLoginLength}
              onSubmitEditing={onSubmit}
              autoFocus={Platform.OS !== 'ios'}
              format={identificatorFormatter}
              props={{
                right: Platform.OS !== 'web' && !enterWithSnils && !confirmBySnils && (
                  <QrCodeIcon onScan={onAutoFill('identificator')} adjustFor='input' />
                ),
              }}
            />
          </View>
        ) : null}
        {enterWithSnils ? (
          <View style={styles.fieldContainer}>
            <Field
              component={BaseField}
              label='Номер карты'
              type='digits'
              name='cardNumber'
              style={isDesktop ? {width: formWidthDesktop, marginBottom: 10, marginTop: 24} : undefined}
              maxLength={defaults.maxLoginLength}
              props={{
                right: Platform.OS !== 'web' && <QrCodeIcon onScan={onAutoFill('cardNumber')} adjustFor='input' />,
              }}
            />
          </View>
        ) : null}
        {confirmByCard && !needAgreement && !needPassword ? (
          <View style={styles.fieldContainer}>
            <Field
              component={BaseField}
              label='СНИЛС'
              type='text'
              name='snils'
              disabled={disabled || needSmsCode}
              readOnly={needSmsCode}
              style={isDesktop ? {width: formWidthDesktop, marginBottom: 10, marginTop: 24} : undefined}
              mask={InputMaskTypes.snils}
            />
          </View>
        ) : null}
        {((confirmByCard && !needSmsCode) || (confirmBySnils && needPhone)) && !needPassword && !needAgreement ? (
          <View style={styles.fieldContainer}>
            <Field
              component={BaseField}
              label='Телефон'
              type='text'
              name='phone'
              style={isDesktop ? {width: formWidthDesktop, marginBottom: 10, marginTop: 24} : undefined}
              mask={InputMaskTypes.tel}
            />
          </View>
        ) : null}
        <View style={isDesktop ? {width: formWidthDesktop, marginBottom: 10, marginTop: 24} : undefined}>
          {needSmsCode ? <SmsFields onRefresh={onRefresh} onSubmit={onSubmit} /> : null}
          {needAgreement ? <Agreement /> : null}
          {needPassword ? <PasswordFields onSubmit={onSubmit} /> : null}
        </View>

        <View style={styles.buttonContainer}>
          <Button
            id='registration-primary'
            disabled={(needSmsCode && isConfirmButtonDisabled) || disabled || submitting}
            type={'transparent'}
            style={styles.button}
            onPress={onSubmit}
            {...buttonProps}
          />
        </View>
      </Form>
    </View>
  );
};

const fl = StyleSheet.flatten;

const createStyles = (styleHelper: StyleHelper): NamedStyles<any> => {
  const {mediaOnly, platformOnly, align} = styleHelper;
  return {
    container: fl([
      mediaOnly(
        DeviceSize.desktop,
        {width: formWidthDesktop},
        {width: Platform.OS === 'web' ? 260 : 284, paddingTop: 18},
      ),
    ]),
    mt8: platformOnly('web', {marginTop: 8}, {marginTop: align(8)}),
    fieldContainer: {
      ...mediaOnly(DeviceSize.desktop, {}, {marginTop: 0}),
    },
    buttonsContainer: fl([
      {
        alignSelf: 'center',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        marginBottom: 0,
        marginTop: 'auto',
      },
      mediaOnly(DeviceSize.desktop, {width: 240, marginTop: 'auto'}, {width: 200, marginTop: 30}),
    ]),
    button: fl([
      {
        alignSelf: 'center',
        marginTop: 'auto',
        marginBottom: 0,
      },
      mediaOnly(
        DeviceSize.desktop,
        {minWidth: 240, width: 'auto', marginTop: 32},
        {
          marginTop: align(20),
          minWidth: 220,
          width: 'auto',
          paddingHorizontal: 4,
        },
      ),
    ]),
  };
};
